import type { Realtime as RealtimeReport } from '@totum-analytics/db';
import { DASHBOARD_POLL_MS } from '@totum-analytics/shared/config';
import { DataTable } from '../components/DataTable';
import { Card, ErrorNote, Loading } from '../components/ui';
import { formatCount, formatRelative } from '../lib/format';
import { useApi } from '../lib/useApi';
import { useSite } from './SiteLayout';

export function Realtime() {
  const { site } = useSite();
  const { data, error, reload } = useApi<RealtimeReport & { active_users_hidden?: boolean }>(
    `/sites/${site.id}/reports/realtime`,
    { pollMs: DASHBOARD_POLL_MS },
  );

  if (error) return <ErrorNote error={error} onRetry={reload} />;
  if (!data) return <Loading label="Looking at the last thirty minutes" />;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-1">
        <p className="text-sm text-gray-600 flex items-center gap-2">
          <span className="relative inline-flex w-2.5 h-2.5" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-60 animate-ping" />
            <span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-green-600" />
          </span>
          Active in the last {data.window_minutes} minutes
        </p>
        <p className="mt-2 text-6xl font-semibold text-gray-900 tabular">{formatCount(data.active_visitors)}</p>
        <p className="mt-2 text-sm text-gray-600 tabular">{formatCount(data.pageviews)} pageviews · refreshes every {DASHBOARD_POLL_MS / 1000} seconds while this tab is open</p>
      </Card>

      <Card title="Pages being viewed" className="lg:col-span-2">
        <DataTable
          rows={data.top_pages}
          rowKey={(r) => r.path}
          empty={<p className="py-6 text-center text-sm text-gray-500">Nobody has viewed a page in the last {data.window_minutes} minutes.</p>}
          columns={[
            { header: 'Page', value: (r) => r.path, render: (r) => <span className="font-mono text-xs text-gray-900 break-all">{r.path}</span> },
            { header: 'Visitors', value: (r) => r.visitors, render: (r) => formatCount(r.visitors), align: 'right', bar: true },
          ]}
        />
      </Card>

      {site.identity_mode === 'identified' && (
        <Card title="Who is active" className="lg:col-span-3">
          {data.active_users_hidden ? (
            <p className="text-sm text-gray-600">Your role can see how many people are active, but not who they are.</p>
          ) : (
            <DataTable
              rows={data.active_users}
              rowKey={(r) => r.user_ref}
              empty={<p className="py-6 text-center text-sm text-gray-500">No signed-in users in the last {data.window_minutes} minutes.</p>}
              columns={[
                { header: 'Person', value: (r) => r.display_name ?? r.user_ref, render: (r) => <span className="text-gray-900">{r.display_name ?? <span className="font-mono text-xs">{r.user_ref}</span>}</span> },
                { header: 'Account', value: (r) => r.account_name ?? r.account_ref ?? '', render: (r) => r.account_name ?? r.account_ref ?? '—' },
                { header: 'Last page', value: (r) => r.path, render: (r) => <span className="font-mono text-xs">{r.path}</span> },
                { header: 'Last seen', value: (r) => r.last_seen_at, render: (r) => formatRelative(r.last_seen_at), align: 'right' },
              ]}
            />
          )}
        </Card>
      )}
    </div>
  );
}
